import { useMutation } from "@apollo/client/react";
import {
  ContentStatus,
  CreateSimulationCaseOutline,
  CreateSimulationCaseOutlineVariables,
} from "__generated__/types";
import { CancelOrSubmit } from "common/buttons";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { Section } from "common/components/Section";
import { markdownInput } from "common/input-types";
import gql from "graphql-tag";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import ReactSelect, { MultiValue } from "react-select";
import {
  clinicalCategories,
  difficulties,
  modules,
} from "../../simulation/utils";
import styles from "./SimulationAdmin.module.css";

const MUTATION = gql`
  mutation CreateSimulationCaseOutline(
    $input: CreateSimulationCaseOutlineInput!
  ) {
    createSimulationCaseOutline(input: $input) {
      simulationCase {
        title
        clinicalCategory
        difficulty
        module
        patientInformation {
          content
          isMarkdown
        }
        status
      }
    }
  }
`;

interface Props {
  cancel(): void;
  complete(): void;
}

interface FormValues {
  title: string;
  patientInfo: string;
  status: string;
  difficulty: string;
  clinicalCategory: string;
  module: string;
}

export const CreateSimulationCase: React.FC<React.PropsWithChildren<Props>> = ({
  cancel,
  complete,
}) => {
  const methods = useForm<FormValues>();

  const [moduleSelected, setModuleSelected] =
    useState<MultiValue<{ value: string; label: string }>>();
  const [difficultySelected, setDifficultySelected] =
    useState<MultiValue<{ value: string; label: string }>>();
  const [categorySelected, setCategorySelected] =
    useState<MultiValue<{ value: string; label: string }>>();

  const [mutate, { error, loading }] = useMutation<
    CreateSimulationCaseOutline,
    CreateSimulationCaseOutlineVariables
  >(MUTATION);

  const onSubmit = async (input: FormValues) => {
    if (!moduleSelected || !difficultySelected || !categorySelected) {
      alert("Please ensure all required fields are selected.");
      return;
    }
    const result = await mutate({
      variables: {
        input: {
          simulationCaseInput: {
            title: input.title,
            patientInformation: markdownInput(input.patientInfo),
            status: ContentStatus.DRAFT,
            module: moduleSelected[0].value,
            difficulty: difficultySelected[0].value,
            clinicalCategory: categorySelected[0].value,
          },
        },
      },
    });

    if (result.data) {
      complete();
    }
  };

  return (
    <Section title="Nytt case">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <TextField
            title="Tittel"
            fieldProps={methods.register("title", {
              required: "Du må gi casen en tittel",
            })}
            placeholder="Navngi tittelen etter demografi og symptomer, e.g: Mann 49, Brystsmerter"
          />

          <p className={styles.selecttext}>
            Moduler, vanskelighetsgrad og kategori
          </p>

          <ReactSelect
            name="module"
            className={styles.reactSelect}
            options={modules}
            placeholder="Du må velge en modul casen skal tilhøre (klikk her for å spesifisere)"
            isMulti={true}
            styles={{
              option: (provided) => ({
                ...provided,
                color: "black",
              }),
            }}
            onChange={setModuleSelected}
          />

          <ReactSelect
            name="difficulty"
            className={styles.reactSelect}
            options={difficulties}
            placeholder="Du må velge en vanskelighetsgrad for casen (klikk her for å spesifisere)"
            isMulti={true}
            styles={{
              option: (provided) => ({
                ...provided,
                color: "black",
              }),
            }}
            onChange={setDifficultySelected}
          />

          <ReactSelect
            name="clinicalCategory"
            className={styles.reactSelect}
            options={clinicalCategories}
            placeholder="Du må velge kategori for casen (klikk her for å spesifisere)"
            isMulti={true}
            styles={{
              option: (provided) => ({
                ...provided,
                color: "black",
              }),
            }}
            onChange={setCategorySelected}
          />

          <MarkdownField<FormValues>
            name="patientInfo"
            title="Beskrivelse av case"
            defaultValue={
              "Her kan du skrive informasjon om pasienten for å veilede studenten, eksempelvis:\n" +
              "Symptomer: Tung pust, økende over en del dager.\n\n" +
              "Sosialt: Hun er gift, har ett barn, jobber som kantineansatt i en større bedrift.\n\n" +
              "Tidligere sykdommer: Hypertensjon, medikamentelt behandlet i fem år med ACE-hemmer og tiazid.\n\n" +
              "KOLS, medikamentelt behandlet med Beta-2-adrenerg agonist i tre år."
            }
          />

          <MutationLoadingOrError loading={loading} error={error} />

          <CancelOrSubmit onCancel={cancel} isLoading={loading} />
        </form>
      </FormProvider>
    </Section>
  );
};
